import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

const MODAL_OVERLAY_CLASS_NAME =
    'fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 h-screen w-full p-2 sm:p-4 overflow-hidden';

const MODAL_PANEL_CLASS_NAME =
    'relative flex flex-col items-center bg-primary-light px-3 sm:px-6 md:px-8 lg:px-[56px] pt-3 sm:pt-4 md:pt-5 pb-4 sm:pb-5 md:pb-6 rounded-[12px] shadow-[0_7px_9px_0_rgba(141,141,141,0.10),0_4px_13px_0_rgba(141,141,141,0.08)] w-full max-w-[98vw] sm:max-w-[90vw] md:max-w-[fit-content] max-h-[98vh] sm:max-h-[95vh] overflow-y-auto overflow-x-hidden z-[100]';

const MODAL_CLOSE_BUTTON_CLASS_NAME =
    'absolute top-3 right-3 sm:top-4 sm:right-4 md:top-5 md:right-5 lg:top-5 lg:right-6 flex items-center justify-center w-[32px] h-[32px] shrink-0 bg-white rounded-[16px] m-0 p-0 hover:bg-gray-100 transition-colors z-10';

const MODAL_DIALOG_ARIA_LABEL = 'Fenêtre modale';

type ModalProps = Readonly<{
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    className?: string;
}>;

export default function Modal({ isOpen, onClose, children, className }: ModalProps) {
    if (!isOpen) {
        return null;
    }

    const panelClassName = className
        ? `${MODAL_PANEL_CLASS_NAME} ${className}`
        : MODAL_PANEL_CLASS_NAME;

    return createPortal(
        <div className={MODAL_OVERLAY_CLASS_NAME} role="presentation">
            <div
                className={panelClassName}
                role="dialog"
                aria-modal="true"
                aria-label={MODAL_DIALOG_ARIA_LABEL}
            >
                <button
                    type="button"
                    aria-label="Fermer"
                    className={MODAL_CLOSE_BUTTON_CLASS_NAME}
                    onClick={onClose}
                >
                    <span className="text-3xl super-heavy leading-none -mt-0.5" aria-hidden="true">
                        &times;
                    </span>
                </button>
                {children}
            </div>
        </div>,
        document.body,
    );
}
